import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Button, Icon, Tab } from '@feathr/components';
import { SessionCacheProvider, useBlackboxVersion, useFundraiser } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl, useStore, useUser } from '@feathr/extender/state';

import { RealtimeDebuggerTrigger } from '../../components/RealtimeDebugger';
import Page from '../Page';
import CreateFormModal from './CreateFormModal';
import FundraisingActivity from './FundraisingActivity';
import DonationFormsTable from './FundraisingDashboard/DonationFormsTable';
import FundraisingSettings from './FundraisingSettings';
import { useFundraiseTracking } from './hooks';

type TFundraiseTab = 'activity' | 'forms' | 'settings';

function FundraisePage(): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();
  const account = useAccount();
  const user = useUser();
  const { tab } = useParams<{ tab: TFundraiseTab }>();
  const history = useHistory();
  const location = useLocation();
  const localUrl = useLocalUrl();
  const { isPending, state } = useFundraiser();
  const hasIntegration = state === 'connected';
  const [isCreateFormModalOpen, { open: openCreateFormModal, close: closeCreateFormModal }] =
    useDisclosure(false);

  const showDevelopmentNavUserSetting = user?.getSetting('show_dev_nav') ?? false;
  const { isOnProduction } = useBlackboxVersion();
  const showRealtimeDebugger =
    !isOnProduction && (account?.isTestAccount || showDevelopmentNavUserSetting);

  useFundraiseTracking(state);

  // Redirect non-integrated users to settings tab (only after data loads)
  useEffect(() => {
    if (!isPending && state !== 'connected' && tab !== 'settings') {
      // Preserve query params (e.g., ?modal=crm&step=3)
      const queryString = location.search ? location.search : '';
      history.replace(localUrl(`/fundraise/settings${queryString}`));
    }
  }, [isPending, state, tab, history, localUrl, location.search]);

  // Only show tabs if set up is complete.
  const tabs = hasIntegration
    ? [
        <Tab key={'activity'} link={localUrl('/fundraise/activity')} title={t('Activity')} />,
        <Tab key={'forms'} link={localUrl('/fundraise/forms')} title={t('Forms')} />,
        <Tab key={'settings'} link={localUrl('/fundraise/settings')} title={t('Settings')} />,
      ]
    : undefined;

  // Tab-conditional actions
  function getActions(): JSX.Element | undefined {
    if (!hasIntegration) {
      return undefined;
    }
    if (tab === 'forms') {
      return (
        <Button onClick={openCreateFormModal} prefix={<Icon.Add />} type={'primary'}>
          {t('Create Form')}
        </Button>
      );
    }
    return undefined;
  }

  const actions = getActions();

  return (
    <SessionCacheProvider>
      <Page
        actions={actions}
        collapseHeader={true}
        includeCurrentPageCrumb={false}
        tabs={hasIntegration ? tabs : []}
        title={t('Fundraising')}
      >
        {tab === 'activity' && <FundraisingActivity />}
        {tab === 'forms' && <DonationFormsTable />}
        {tab === 'settings' && <FundraisingSettings />}
      </Page>
      {isCreateFormModalOpen && (
        <CreateFormModal
          // TODO: Don't pass defaults once the backend is updated to set defaults
          form={DonationForms.create({
            // @ts-ignore will be removed once the backend is updated to set defaults
            config: { fields: { 'donation-amount': { properties: { options: [] } } } },
          })}
          onClose={closeCreateFormModal}
          opened={isCreateFormModalOpen}
        />
      )}
      {showRealtimeDebugger && <RealtimeDebuggerTrigger />}
    </SessionCacheProvider>
  );
}

export default observer(FundraisePage);
